import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { ShootingSchedule } from '../components/ShootingSchedule';
import { fetchStripBoard } from '../../strip-board/strip-board.slice';
import { ScreenContainer } from '../../projects/components/styled-project-components';
import { LoaderContainer } from '../../../routes/project/components/step-container';
import { PageLoader } from '../../../components/page-loader/PageLoader';
import { ScheduleHeader } from '../components/schedule-screen.header';
import { StepDrawer } from '../../projects/components/step.drawer';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { useCurrentProject } from '../../../hooks/use-current-project';
import { uiStore } from '../../../core/stores/ui-store';
import { PropertiesPanel } from '../../properties/components/properties-panel';
import { context } from '../../../app/context';
import { ShootingDay, Shot } from '../../../app/entities';
import { breakPoint } from '../../../app/theme';

import type { ScreenProps } from '../../projects/models/types';

export const ScheduleScreen: React.FC<ScreenProps> = observer(({ step }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isShowingLimitsModal, setIsShowingLimitsModal] = useState(false);
  const currentMember = context.member;
  const project = useCurrentProject()!;

  /**
   * Always close the property drawer when navigating away from the screen
   */
  useEffect(() => {
    return () => {
      uiStore.closePropertiesPanel();
    };
  }, [step]);

  useEffect(() => {
    fetchStripBoard(project._id).then(() => {
      setIsLoading(false);
    });
  }, [project]);

  const stripboard = project.stripboard;
  const view = step.resource;

  if (isLoading || !stripboard || !view) {
    return (
      <LoaderContainer>
        <PageLoader />
      </LoaderContainer>
    );
  }

  const shootingDays = stripboard.shootingDays;
  const totalShootingDays = stripboard.totalShootingDays;
  const unscheduledStrips = stripboard.unscheduledStrips;
  const stripIds: string[] = stripboard.strips.map((strip: any) => strip._id);
  const unassignedStripIds = stripboard.unscheduledStrips.map((strip) => strip._id);
  const sortableStripIds = [
    ...stripboard.sortableStripIds,
    'unscheduled-strips-header',
    ...unassignedStripIds,
  ];

  const handleAddShotToShootingDay = (shootingDay: ShootingDay) => {
    const endOfDayStrip = shootingDay.strips[shootingDay.strips.length - 1];

    Shot.createShot({
      projectId: project._id,
      stripPosition: stripboard.getPreviousShootingDayPosition(endOfDayStrip._id),
      shootingDayId: shootingDay._id,
    });
  };

  const getStripPosition = (stripIndex: number) => {
    if (stripIndex < 0 || stripIndex >= stripIds.length) return undefined;

    const destination = stripboard.strips[stripIndex];

    if (destination === undefined) {
      return;
    }

    return destination.position;
  };

  const onUpdateStrip = async (
    stripId: string,
    update: {
      data?: {
        text?: string;
        estimatedTime?: number;
        isHidden?: boolean;
      };
    },
  ) => {
    await stripboard.editStrip(stripId, update);
  };

  const onResortStrips = async (selectedStripIds: string[], newPosition: number) => {
    await stripboard.edit(selectedStripIds, newPosition);
  };

  const userStepPreferences = currentMember?.preferences.stepsUI[step._id];

  return (
    <React.Fragment>
      <ScheduleHeader step={step} project={project} />

      <StepDrawer step={step} />

      {isLoading ? (
        <LoaderContainer>
          <PageLoader />
        </LoaderContainer>
      ) : (
        <CustomScreenContainer>
          {stripboard ? (
            <ShootingSchedule
              options={userStepPreferences}
              stripboard={stripboard}
              strips={stripboard.strips}
              properties={view.visibleProperties}
              shootingDays={shootingDays}
              project={project}
              stripIds={stripIds}
              sortableStripIds={sortableStripIds}
              totalShootingDays={totalShootingDays}
              unscheduledStrips={unscheduledStrips}
              onResortStrips={onResortStrips}
              onUpdateStrip={onUpdateStrip}
              onAddShotToShootingDay={handleAddShotToShootingDay}
              getStripPosition={getStripPosition}
            />
          ) : null}
        </CustomScreenContainer>
      )}

      {isShowingLimitsModal && (
        <LimitReachedModal
          type="shots"
          eventName="Shots exceed 10"
          onCancel={() => setIsShowingLimitsModal(false)}
        />
      )}

      {uiStore.isPropertiesPanelOpen && !!uiStore.propertiesPanelTargetId && (
        <Drawer>
          <PropertiesPanel targetId={step.resource._id} view={step.resource} />
        </Drawer>
      )}
    </React.Fragment>
  );
});

const CustomScreenContainer = styled(ScreenContainer)`
  padding: 1.6rem 1.2rem 1.6rem 1.6rem;

  @media screen and (min-width: ${breakPoint.medium}px) {
    overflow: hidden;
    padding: 0;
  }
`;

const Drawer = styled.div`
  position: absolute;
  top: 4rem;
  right: 0;
  bottom: 0;
  min-width: 26.5rem;
  width: 25%;
  z-index: 1000;
`;
